import { AsYouType, getCountries, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import { useCountries } from 'contexts/CountriesContext';
import { FieldError } from 'react-hook-form';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { Avatar, FormControl, FormLabel, IconButton, InputAdornment, ListItemText, Menu, MenuItem, TextField, Typography } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import ClearIcon from '@mui/icons-material/ClearOutlined';
import { useEffect, useMemo, useState } from 'react';
import { interleaveMenuDivider } from 'utils/interleaveDivider';

interface StandardPhoneNumberFieldProps {
  label: string;
  disabled: boolean;
  value: string;
  onChange: ControllerRenderProps['onChange'];
  error?: FieldError;
  name: string;
  readOnly: boolean;
  /* ISO 3166-2 code */
  defaultCountry?: string;
  lockCountryCode?: boolean;
}

const StandardPhoneNumberField = ({
  label,
  disabled,
  value,
  onChange,
  error,
  name,
  defaultCountry,
  readOnly,
  lockCountryCode = false,
}: StandardPhoneNumberFieldProps) => {
  const [countryCode, setCountryCode] = useState<string>(defaultCountry || '');
  const [internalValue, setInternalValue] = useState<string>(value || (defaultCountry ? `+${getCountryCallingCode(defaultCountry as any)}` : ''));
  const { countriesList } = useCountries();
  const countryNamesByCode = useMemo(() => countriesList.reduce((acc, country) => acc.set(country.iso_3166_2, country.name), new Map()), [countriesList]);

  const countryOptions = useMemo(
    () =>
      getCountries()
        .map((countryCode) => ({ countryName: countryNamesByCode.get(countryCode), countryCode }))
        .filter((option) => option.countryName)
        .sort((a, b) => a.countryName.toLowerCase().localeCompare(b.countryName.toLowerCase())),
    [countryNamesByCode],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClickFlagAvatar = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!lockCountryCode) setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const selectCountry = (countryCode) => {
    handleClose();
    setCountryCode(countryCode);
    setInternalValue(`+${getCountryCallingCode(countryCode)}`);
  };

  const onClear = () => {
    const emptyValue = lockCountryCode ? `+${getCountryCallingCode(defaultCountry as any)}` : '';
    setInternalValue(emptyValue);
    setCountryCode(defaultCountry || '');
    onChange(emptyValue);
  };

  useEffect(() => {
    if (value && isValidPhoneNumber(value)) {
      const parsedPhoneNumber = parsePhoneNumberWithError(value);
      setCountryCode(parsedPhoneNumber.country || '');
      setInternalValue(parsedPhoneNumber.formatInternational());
    }
  }, [value]);

  return (
    <>
      <FormControl fullWidth size="small" data-cy-field-type="standard-phone-number">
        <FormLabel id={`${name}-label`}>{label}</FormLabel>
        <TextField
          size="small"
          type="text"
          aria-labelledby={`${name}-label`}
          value={internalValue}
          error={!!error}
          placeholder="Enter phone number (including country code)"
          inputProps={{ readOnly }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Avatar
                  id={`${name}-standard-phone-number-country-code-selector`}
                  aria-controls={open ? 'school-selector-menu' : undefined}
                  aria-expanded={open ? 'true' : undefined}
                  aria-haspopup
                  src={`/images/flag-icons/flags/4x3/${countryCode.toLowerCase()}.svg`}
                  sx={{ width: 26, height: 26, fontSize: '16px', cursor: lockCountryCode ? 'auto' : 'pointer' }}
                  onClick={handleClickFlagAvatar}
                >
                  <PhoneIcon sx={{ fontSize: '16px' }} />
                </Avatar>
                <Menu
                  id={`${name}-standard-phone-number-menu`}
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  MenuListProps={{ 'aria-labelledby': `${name}-standard-phone-number-country-code-selector` }}
                >
                  {interleaveMenuDivider(
                    countryOptions.map((countryOption, index) => (
                      <MenuItem key={countryOption.countryCode} onClick={() => selectCountry(countryOption.countryCode)} tabIndex={index}>
                        <ListItemText>{countryOption.countryName}</ListItemText>
                      </MenuItem>
                    )),
                  )}
                </Menu>
              </InputAdornment>
            ),
            endAdornment: (
              <IconButton onClick={onClear} sx={{ visibility: internalValue ? 'visible' : 'hidden' }}>
                <ClearIcon />
              </IconButton>
            ),
          }}
          disabled={disabled}
          onChange={(event) => {
            let newValue = event.target.value;

            // Keep country code prefix if lockCountryCode is true
            if (lockCountryCode && defaultCountry) {
              const prefix = `+${getCountryCallingCode(defaultCountry as any)}`;
              if (!newValue.startsWith(prefix)) {
                const suffix = newValue.slice(prefix.length);
                newValue = prefix + suffix;
              }
            }

            if (!newValue) return onClear();
            const prefixedValue = internalValue === '' && !defaultCountry ? `+${newValue}` : newValue;
            const formattedPartial = new AsYouType((defaultCountry as any) || undefined).input(prefixedValue);
            if (isValidPhoneNumber(formattedPartial)) {
              const parsedPhoneNumber = parsePhoneNumberWithError(formattedPartial);
              const formatInternational = parsedPhoneNumber.formatInternational();
              setCountryCode(parsedPhoneNumber.country || '');
              setInternalValue(formatInternational);
              onChange(formatInternational);
            } else {
              setInternalValue(formattedPartial);
              setCountryCode('');
            }
          }}
        />
      </FormControl>
      {error ? <Typography color="error">{error!.message as string}</Typography> : <br />}
    </>
  );
};

export default StandardPhoneNumberField;
